/**
 *
 * Param Channel
 *
 * The current value of a live param, held outside Redux: one flat map keyed by qualified param (`diffusion.strength`), a per-key subscriber list, and a dirty set drained by a 30Hz flush.
 *
 * A LAYER, never a mode — the channel is the home for a current value in every phase, so a reader never branches on which source is authoritative. Redux keeps the settled snapshot.
 *
 * ⚠️ Writes do NOT notify; the flush does. That is what makes a 120Hz pointer cost what a 60Hz one does.
 *
 * See context/param-channel.md.
 *
 */
import { DROP, settleValue } from '~/lib/params'
import { EFFECT_SCHEMAS } from '~/schemas/effect_schemas'
import { isMorphEnvelope } from '~/schemas/morph'

// The UI's fixed rate. A worker clock, not rAF: rAF is throttled in a background tab, and measured at 25.7Hz against the worker's 58.8Hz even in the foreground under load.
export const UI_FLUSH_FPS = 30

const EFFECT_NAMES = new Set(Object.keys(EFFECT_SCHEMAS))

/** Split a qualified key once. Null for anything that is not `effect.param`, so a malformed key is dropped rather than written under a bogus section. */
const splitKey = key => {
	const dot = key.indexOf('.')
	if (dot <= 0 || dot === key.length - 1) return null
	return [key.slice(0, dot), key.slice(dot + 1)]
}

class ParamChannel {
	constructor() {
		// Qualified key → current value. Flat, so a write is one Map.set and no object churn.
		this._values = new Map()
		// Called once per flush that delivered anything, whatever moved. The per-key subscribers answer
		// "what is this param now"; an observer answers "the channel moved" — which is what a sender
		// diffing the whole snapshot needs, and what no per-key subscription can say without
		// subscribing to every key that might ever exist.
		this._observers = new Set()
		// Qualified key → Set of subscriber fns.
		this._subs = new Map()
		// Keys written since the last flush. Only these are delivered.
		this._dirty = new Set()
		this._worker = null
		// Guards against a subscriber that writes the channel: the write lands for the NEXT flush rather than extending this one into an unbounded loop.
		this._flushing = false
	}

	/**
	 * Write a value, marking it for the next flush.
	 *
	 * ⚠️ Runs the WRITE RULE (`settleValue`), the same one the reducer and every save apply — the channel is a carrier of `live`, so a value it holds must be one storage could hold.
	 */
	set(key, value) {
		const parts = splitKey(key)
		if (!parts) return
		// A morph envelope is a VALUE here, carried whole for the wire — the rule would compare an object against a scalar initial and never drop it, so it is exempted rather than mangled.
		//
		// ⚠️ Gated on the contract's `morph` FLAG rather than the shape, so the exemption names WHY it applies. `isMorphEnvelope` is deliberately just "is an object" (`src/schemas/morph.js`); the two agree today only because `quantize` passes a non-number through untouched, so a shape test happens to reach the same result by a route that stops being true the moment the rule handles objects.
		const prop = EFFECT_SCHEMAS[parts[0]]?.properties?.[parts[1]]
		const settled = prop?.morph && isMorphEnvelope(value) ? value : settleValue(prop, value)
		if (settled === DROP) {
			if (!this._values.has(key)) return
			this._values.delete(key)
		} else {
			if (this._values.get(key) === settled) return
			this._values.set(key, settled)
		}
		this._dirty.add(key)
	}

	/** Write a whole `live`-shaped patch. Effect sections only — `objects` and `effects` are not param keys and never enter the channel. */
	patch(patch) {
		if (!patch) return
		for (const effect in patch) {
			if (!EFFECT_NAMES.has(effect)) continue
			const section = patch[effect]
			if (!section || typeof section !== 'object' || Array.isArray(section)) continue
			for (const param in section) this.set(`${effect}.${param}`, section[param])
		}
	}

	/** The current value, whatever the phase. A morphing param yields its ENVELOPE — `useLiveValue` is what coerces to a scalar. */
	get(key) {
		return this._values.get(key)
	}

	has(key) {
		return this._values.has(key)
	}

	/** Remove a key, dirtying it so subscribers see it fall back to its initial. */
	delete(key) {
		if (!this._values.delete(key)) return
		this._dirty.add(key)
	}

	/** The param names currently held under one effect. Materialized, so a caller may delete while iterating. */
	keys(effect) {
		const prefix = `${effect}.`
		const out = []
		for (const key of this._values.keys()) if (key.startsWith(prefix)) out.push(key.slice(prefix.length))
		return out
	}

	/** Register an imperative subscriber for one key. Returns its unsubscribe. */
	subscribe(key, fn) {
		let set = this._subs.get(key)
		if (!set) this._subs.set(key, (set = new Set()))
		set.add(fn)
		return () => {
			const s = this._subs.get(key)
			if (!s) return
			s.delete(fn)
			if (!s.size) this._subs.delete(key)
		}
	}

	/**
	 * Observe FLUSHES rather than one key. Returns its unsubscribe.
	 *
	 * ⚠️ For a reader whose unit is the whole snapshot, never a single param — the wire sender is the
	 * one that matters: it diffs `withChannelParams(live)` at send time, so it needs to know that
	 * something moved, not what.
	 */
	observe(fn) {
		this._observers.add(fn)
		return () => this._observers.delete(fn)
	}

	/**
	 * The whole map shaped back into nested `live` form.
	 *
	 * ⚠️ Nested because `buildWirePatch` walks STRUCTURALLY and needs a `live`-shaped object on both sides to diff. A morph envelope passes through whole — flattening one here puts a scalar on the wire where the server expects `{a,b,t}`.
	 */
	snapshot() {
		const out = {}
		for (const [key, value] of this._values) {
			const parts = splitKey(key)
			if (!parts) continue
			;(out[parts[0]] ??= {})[parts[1]] = value
		}
		return out
	}

	/** Deliver every dirty key to its subscribers and clear the set. Called by the flush clock; safe to call by hand in a test. */
	flush() {
		if (!this._dirty.size || this._flushing) return
		this._flushing = true
		// Drained BEFORE delivery, so a subscriber's own write marks the key for the next flush instead of being cleared unseen by this one.
		const keys = [...this._dirty]
		this._dirty.clear()
		try {
			for (const key of keys) {
				const subs = this._subs.get(key)
				if (!subs) continue
				const value = this._values.get(key)
				// A throwing subscriber must not strand the rest of the frame's keys undelivered.
				for (const fn of subs) {
					try {
						fn(value, key)
					} catch (e) {
						console.error('[paramChannel] subscriber failed', key, e)
					}
				}
			}
		} finally {
			this._flushing = false
		}
		// ⚠️ AFTER the per-key delivery and outside the guard: an observer that writes the channel must
		// mark its key for the NEXT flush, exactly as a subscriber does, rather than extending this one.
		for (const fn of this._observers) {
			try {
				fn()
			} catch (e) {
				console.error('[paramChannel] flush observer failed', e)
			}
		}
	}

	/**
	 * Start the flush clock. Idempotent — a second call is a no-op rather than a second worker.
	 *
	 * ⚠️ No `Worker` is a supported condition: writes still land and `flush()` stays callable by hand.
	 */
	start(fps = UI_FLUSH_FPS) {
		if (this._worker || typeof Worker === 'undefined') return
		this._worker = new Worker(new URL('./frameClockWorker.js', import.meta.url))
		this._worker.onmessage = () => this.flush()
		this._worker.postMessage({ type: 'start', fps })
	}

	stop() {
		if (!this._worker) return
		this._worker.postMessage({ type: 'stop' })
		this._worker.terminate()
		this._worker = null
	}

	/** Drop every value and pending key. Subscribers survive — a slider stays mounted across a project switch. */
	clear() {
		// Dirtied, not just dropped: a surviving subscriber is told the key is gone and falls back to its initial. Clearing the set instead leaves it showing the old project's value.
		for (const key of this._values.keys()) this._dirty.add(key)
		this._values.clear()
	}
}

const paramChannel = new ParamChannel()

export default paramChannel
